import { Reveal } from "@/components/Reveal";
import { Building2, Compass, Target, Heart, Award, ShieldCheck } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";
import { useSiteContent } from "@/context/SiteContentContext";

const ICONS = { Building2, Compass, Target, Heart, Award, ShieldCheck };
const Icon = ({ name, ...rest }) => {
  const C = ICONS[name] || Building2;
  return <C {...rest} />;
};

export default function About() {
  const { content } = useSiteContent();
  const c = content.about_page;

  return (
    <div data-testid="about-page" className="bg-[var(--ivory)] pt-32 lg:pt-40 pb-24 min-h-screen">
      <section className="relative px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -left-20 w-96 h-96 border border-[var(--gold)]/15 rounded-full" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <span className="eyebrow">{c.hero.eyebrow}</span>
            <h1 className="font-serif-luxe text-5xl lg:text-6xl mt-4 text-[var(--navy)] leading-[1.05]">
              {c.hero.heading_line1} <span className="italic text-[var(--gold)]">{c.hero.heading_italic}</span>
            </h1>
            <div className="gold-divider w-24 my-6" />
            <p className="text-[var(--slate)] text-base lg:text-lg font-light leading-relaxed whitespace-pre-line">{c.hero.body_p1}</p>
            <p className="mt-4 text-[var(--slate)] font-light leading-relaxed whitespace-pre-line">{c.hero.body_p2}</p>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={150}>
            <div className="relative arch-frame">
              <div className="overflow-hidden arch-top">
                <img src={resolveImageUrl(c.hero.image)} alt="Özkayalar İnşaat" className="w-full h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 w-32 h-32 border border-[var(--gold)] rounded-tl-full pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-[var(--cream)] mt-24 py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="eyebrow">{c.founding_story.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)]">
              {c.founding_story.heading_line1} <span className="italic text-[var(--gold)]">{c.founding_story.heading_italic}</span>
            </h2>
            <div className="gold-divider w-20 mx-auto my-6" />
            <p className="text-[var(--slate)] leading-relaxed font-light text-base lg:text-lg whitespace-pre-line">{c.founding_story.body_p1}</p>
            <p className="mt-4 text-[var(--slate)] leading-relaxed font-light whitespace-pre-line">{c.founding_story.body_p2}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6 lg:gap-8">
          {c.pillars.map((p, i) => (
            <Reveal key={i} delay={i * 100}>
              <div
                data-testid={`pillar-${i}`}
                className={`p-8 lg:p-10 border relative overflow-hidden h-full ${
                  i % 3 === 1 ? "bg-[var(--navy)] text-[var(--ivory)] border-[var(--gold)]/30" : "bg-[var(--ivory)] text-[var(--ink)] border-[var(--gold)]/30"
                }`}
              >
                <span className="absolute top-3 right-3 w-8 h-8 border-t border-r border-[var(--gold)]" />
                <div className={`w-12 h-12 mb-6 border flex items-center justify-center ${i % 3 === 1 ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--navy)] text-[var(--navy)]"}`}>
                  <Icon name={p.icon} size={20} />
                </div>
                <h3 className={`font-serif-luxe text-3xl mb-3 ${i % 3 === 1 ? "text-[var(--ivory)]" : "text-[var(--navy)]"}`}>{p.title}</h3>
                <p className={`text-sm font-light leading-relaxed whitespace-pre-line ${i % 3 === 1 ? "text-[var(--ivory)]/80" : "text-[var(--slate)]"}`}>{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative bg-[var(--cream)] py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {c.award_stats.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="text-center lux-card p-8" data-testid={`about-stat-${i}`}>
                <Icon name={s.icon} size={22} className="mx-auto text-[var(--gold)]" />
                <div className="font-serif-luxe text-4xl text-[var(--navy)] mt-3">{s.number}</div>
                <div className="text-xs tracking-[0.18em] uppercase text-[var(--slate)] mt-2">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
